import {
  ToccoSeminarBaseModulesQueryDocument,
  ToccoSeminarBaseModulesQueryQuery,
  ToccoSeminarBaseModulesQueryQueryVariables,
} from '~/api/graphql/generated'
import { useTypedApolloClients } from '~/lib/utils/graphqlDocument'
import { normalizeDurationDays } from '~/lib/utils/toccoUtils'

export interface ToccoEventItem {
  id: string
  entityId: string
  title: string
  type: string
  teaser: string
  duration: string
  durationDays: number | null
  url: string
}

export interface ToccoSeminarModuleItem {
  id: string
  title: string
  durationDays: number | null
  optional: boolean
}

export const dedupeSeminarModules = (
  required: ToccoSeminarModuleItem[],
  optional: ToccoSeminarModuleItem[],
): ToccoSeminarModuleItem[] => {
  const requiredIds = new Set(required.map((module) => module.id))
  return [...required, ...optional.filter((module) => !requiredIds.has(module.id))]
}

export const COURSE_EVENT_TYPES = ['Kurs', 'Webinar', 'Einführungsmodul']
export const SEMINAR_EVENT_TYPES = ['Lehrgang']
export const TOCCO_EVENT_ENVIRONMENTS = ['physical', 'online', 'e-learning']
export const TOCCO_EVENT_FILTER_ALL = 'all'

interface SeminarModuleReference {
  entity?: {
    id?: number | null
    title?: string | null
    status?: boolean | null
    fieldDurationDays?: number | string | null
  } | null
}

interface SeminarModulesEntity {
  id?: number | null
  fieldRequiredBaseModules?: Array<SeminarModuleReference | null> | null
  fieldOptionalBaseModules?: Array<SeminarModuleReference | null> | null
}

const mapModules = (
  references: Array<SeminarModuleReference | null> | null | undefined,
  optional: boolean,
): ToccoSeminarModuleItem[] =>
  (references ?? [])
    .map((reference) => reference?.entity)
    .filter((module): module is NonNullable<SeminarModuleReference['entity']> => Boolean(module?.status))
    .map((module) => ({
      id: (module.id ?? '').toString(),
      title: module.title ?? '',
      durationDays: normalizeDurationDays(module.fieldDurationDays),
      optional,
    }))

export const useToccoEvents = async ({
  key,
  types,
  pageSize,
  q,
  category,
  targetGroup,
  environment,
  withModules,
}: {
  key: string
  types: string[] | Ref<string[]> | ComputedRef<string[]>
  pageSize: number
  q?: Ref<string> | ComputedRef<string>
  category?: Ref<string> | ComputedRef<string>
  targetGroup?: Ref<string> | ComputedRef<string>
  environment?: Ref<string[]>
  withModules?: boolean
}) => {
  const { languageCode } = useLocale()
  const clients = useTypedApolloClients()

  const page = ref(1)

  const fetchModules = async (items: ToccoEventItem[]) => {
    const modules: Record<string, ToccoSeminarModuleItem[]> = {}
    if (!items.length || !clients?.default) return modules

    let result
    try {
      result = await clients.default.query<
        ToccoSeminarBaseModulesQueryQuery,
        ToccoSeminarBaseModulesQueryQueryVariables
      >({
        query: ToccoSeminarBaseModulesQueryDocument,
        variables: {
          ids: items.map((item) => item.entityId),
          limit: items.length,
        },
      })
    } catch {
      return modules
    }

    for (const entity of result.data.toccoQuery?.entities ?? []) {
      const seminar = entity as SeminarModulesEntity | null
      if (!seminar?.id) continue
      modules[seminar.id.toString()] = dedupeSeminarModules(
        mapModules(seminar.fieldRequiredBaseModules, false),
        mapModules(seminar.fieldOptionalBaseModules, true),
      )
    }
    return modules
  }

  const { data, status, refresh } = await useAsyncData(`${key}-${languageCode.value}`, async () => {
    const response = await $fetch<{ hits?: ToccoEventItem[]; total?: number }>('/api/elasticsearch/tocco-events', {
      method: 'POST',
      body: {
        langcode: languageCode.value,
        from: pageSize * (page.value - 1),
        size: pageSize,
        types: toValue(types),
        ...(toValue(q) ? { q: toValue(q) } : {}),
        ...(category?.value ? { category: category.value } : {}),
        ...(targetGroup?.value ? { targetGroup: targetGroup.value } : {}),
        ...(environment && environment.value.length < TOCCO_EVENT_ENVIRONMENTS.length
          ? { environment: environment.value }
          : {}),
      },
    })

    const items = response.hits ?? []
    return {
      items,
      total: response.total ?? 0,
      modules: withModules ? await fetchModules(items) : {},
    }
  })

  const filters = [q, category, targetGroup, environment].filter(Boolean) as Ref<unknown>[]
  if (isRef(types)) {
    filters.push(types as Ref<unknown>)
  }
  if (filters.length) {
    watch(filters, async () => {
      if (page.value !== 1) {
        page.value = 1
        return
      }
      await refresh()
    })
  }
  watch(page, async () => await refresh())

  return {
    items: computed(() => data.value?.items ?? []),
    total: computed(() => data.value?.total ?? 0),
    modules: computed(() => data.value?.modules ?? {}),
    pages: computed(() => Math.ceil((data.value?.total ?? 0) / pageSize)),
    loading: computed(() => status.value === 'pending'),
    page,
  } as const
}
